import { jsx as _jsx } from "react/jsx-runtime";
// Per-browser user preferences. Stored in localStorage so they survive
// reloads without us having to round-trip through the server. Anything that
// must move with the user across devices belongs on the backend instead.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from "react";
const STORAGE_KEY = "node-one.prefs.v1";
const DEFAULTS = {
    theme: "system",
    editorMode: "split",
    autosaveDelayMs: 2500,
    versionsToShow: 10,
    importFolderAsCategory: true,
    importAutoApply: true,
    experimentalCollab: false,
    debugLogs: "default",
    aiConfig: {
        readmes: { provider: "auto", model: "auto" },
    },
    customCategories: [],
};
function readStored() {
    if (typeof window === "undefined")
        return DEFAULTS;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return DEFAULTS;
        const parsed = JSON.parse(raw);
        return {
            ...DEFAULTS,
            ...parsed,
            autosaveDelayMs: typeof parsed.autosaveDelayMs === "number"
                ? Math.min(10000, Math.max(500, parsed.autosaveDelayMs))
                : DEFAULTS.autosaveDelayMs,
            versionsToShow: typeof parsed.versionsToShow === "number"
                ? Math.min(50, Math.max(1, parsed.versionsToShow))
                : DEFAULTS.versionsToShow,
        };
    }
    catch {
        return DEFAULTS;
    }
}
const PreferencesContext = createContext(null);
function applyTheme(theme) {
    if (typeof document === "undefined")
        return;
    const resolved = theme === "system"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light"
        : theme;
    document.documentElement.classList.toggle("dark", resolved === "dark");
    document.documentElement.dataset.theme = resolved;
}
export function PreferencesProvider({ children }) {
    const [prefs, setPrefs] = useState(() => readStored());
    useEffect(() => {
        applyTheme(prefs.theme);
        if (prefs.theme !== "system")
            return;
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = () => applyTheme("system");
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, [prefs.theme]);
    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
        }
        catch {
            // localStorage may be unavailable (private mode). Silent is fine.
        }
    }, [prefs]);
    const setPref = useCallback((key, value) => {
        setPrefs((prev) => ({ ...prev, [key]: value }));
    }, []);
    const resetPrefs = useCallback(() => setPrefs(DEFAULTS), []);
    const value = useMemo(() => ({ prefs, setPref, resetPrefs }), [prefs, setPref, resetPrefs]);
    return (_jsx(PreferencesContext.Provider, { value: value, children: children }));
}
export function usePreferences() {
    const ctx = useContext(PreferencesContext);
    if (!ctx)
        throw new Error("usePreferences must be used inside PreferencesProvider.");
    return ctx;
}
